import {
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';

import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexTitleSubtitle,
  ApexStroke,
  ApexGrid,
} from 'ng-apexcharts';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
};

@Component({
  selector: 'app-view-chart',
  templateUrl: './view-chart.component.html',
  styleUrl: './view-chart.component.scss',
})
export class ViewChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() chartOptions: Partial<ChartOptions> | any;
  @ViewChild('chart') chart: ChartComponent;

  constructor(private cdr: ChangeDetectorRef) {}

  private isViewInitialized = false;
  private isDestroyed = false;

  /**
   * Gates <apx-chart>. ng-apexcharts creates its ApexCharts instance
   * asynchronously (`await import('apexcharts')`) and, on any later
   * ngOnChanges where only `series` changed by reference, takes an
   * `updateSeries()` shortcut instead of re-creating the chart. Mounting with
   * a placeholder `series: []` therefore races: the real dataset arrives while
   * `chartInstance` is still null, `updateSeries()` no-ops on it, and the
   * chart stays empty until an unrelated input (e.g. the type dropdown)
   * forces a re-create. Mounting only once real series exist avoids the race
   * entirely, so the first render already carries the data.
   */
  public hasRenderableOptions = false;

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    // If chartOptions are already set, ensure the chart renders properly
    if (this.chartOptions && !this.isDestroyed) {
      setTimeout(() => {
        if (!this.isDestroyed) {
          this.cdr.detectChanges();
        }
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartOptions'] && this.chartOptions) {
      const chartType = this.chartOptions?.chart?.type;
      // Only apply bar chart specific settings for bar charts
      if (chartType === 'bar') {
        this.applyBarChartDataLabelTopPosition();
      }

      const wasMounted = this.hasRenderableOptions;
      this.hasRenderableOptions = this.hasSeriesData(this.chartOptions);

      // Pushing options imperatively is only meaningful for an already-mounted
      // chart; on the mounting pass ng-apexcharts builds the instance from the
      // bound inputs, which already carry these settings.
      if (wasMounted && this.hasRenderableOptions && chartType === 'bar') {
        this.pushOptionsToChart();
      }
    }
  }

  /**
   * True once `chartOptions` carries at least one real data point. Both shapes
   * ApexCharts accepts are handled: axis charts hold `[{ name, data }]`, pie
   * charts hold a plain number array.
   */
  private hasSeriesData(options: any): boolean {
    const series = options?.series;
    if (!Array.isArray(series) || series.length === 0) {
      return false;
    }
    return series.some((entry: any) =>
      Array.isArray(entry?.data) ? entry.data.length > 0 : entry !== null && entry !== undefined,
    );
  }

  // removed view init update

  private applyBarChartDataLabelTopPosition(): void {
    try {
      const isBarChart =
        this.chartOptions?.chart?.type === 'bar' || this.chartOptions?.plotOptions?.bar;
      if (!isBarChart) {
        return;
      }

      const isHorizontalBar = this.chartOptions?.plotOptions?.bar?.horizontal === true;

      this.chartOptions.plotOptions = this.chartOptions.plotOptions || {};
      this.chartOptions.plotOptions.bar = this.chartOptions.plotOptions.bar || {};

      // Preserve horizontal setting if it exists
      if (!this.chartOptions.plotOptions.bar.horizontal && !isHorizontalBar) {
        this.chartOptions.plotOptions.bar.horizontal = false;
      } else if (isHorizontalBar) {
        this.chartOptions.plotOptions.bar.horizontal = true;
      }

      this.chartOptions.plotOptions.bar.dataLabels =
        this.chartOptions.plotOptions.bar.dataLabels || {};
      this.chartOptions.plotOptions.bar.dataLabels.position = 'top';

      this.chartOptions.dataLabels = this.chartOptions.dataLabels || {};
      if (typeof this.chartOptions.dataLabels.enabled === 'undefined') {
        this.chartOptions.dataLabels.enabled = true;
      }
      if (typeof this.chartOptions.dataLabels.offsetY === 'undefined') {
        this.chartOptions.dataLabels.offsetY = -10;
      }
      if (typeof this.chartOptions.dataLabels.formatter !== 'function') {
        this.chartOptions.dataLabels.formatter = (value: any) => `${value}`;
      }
      // keep style if set externally
    } catch {
      // fail-safe: do nothing if structure is unexpected
    }
  }

  private pushOptionsToChart(): void {
    try {
      if (!this.chart || !this.chartOptions || this.isDestroyed) {
        return;
      }
      const chartType = this.chartOptions?.chart?.type;
      // Only update bar charts
      if (chartType !== 'bar') {
        return;
      }

      const isHorizontalBar = this.chartOptions?.plotOptions?.bar?.horizontal === true;
      const optionsToApply: any = {
        plotOptions: {
          bar: {
            dataLabels: { position: 'top' },
            horizontal: isHorizontalBar,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -16,
          textAnchor: 'end',
        },
        xaxis: {
          labels: {
            show: true,
          },
        },
      };

      // For horizontal bars, update yaxis with categories
      if (isHorizontalBar && this.chartOptions.yaxis?.categories) {
        optionsToApply.yaxis = {
          categories: this.chartOptions.yaxis.categories,
          max: this.chartOptions.yaxis.max,
        };
      }

      if (!this.isDestroyed) {
        this.chart.updateOptions(optionsToApply, false, true, true);
        this.cdr.detectChanges();
      }
    } catch {
      // ignore update errors
    }
  }

  public forceUpdateHorizontalBar(retriesLeft: number = 10): void {
    try {
      if (this.isDestroyed || !this.chartOptions) {
        return;
      }
      // The <apx-chart> child is gated on real series data and builds its
      // ApexCharts instance asynchronously, so on a first load the ViewChild
      // can still be unresolved here. Retry briefly instead of dropping the
      // update, which used to leave horizontal bars unrendered until the user
      // touched the type dropdown.
      if (!this.chart) {
        if (retriesLeft > 0) {
          setTimeout(() => this.forceUpdateHorizontalBar(retriesLeft - 1), 50);
        }
        return;
      }
      const isHorizontalBar = this.chartOptions?.plotOptions?.bar?.horizontal === true;
      if (isHorizontalBar && !this.isDestroyed) {
        const optionsToApply = {
          plotOptions: {
            bar: {
              horizontal: true,
            },
          },
          yaxis: this.chartOptions.yaxis,
        } as any;
        this.chart.updateOptions(optionsToApply, false, true, true);
        this.cdr.detectChanges();
      }
    } catch {
      // ignore update errors
    }
  }
}
